import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IPicklist } from '@app/models/picklist.interface';
import { first, map, take, tap } from 'rxjs/operators';
import { Router } from "@angular/router"
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { AreasService } from '@app/services/areas/areas.service';
import { CategoriesService } from '@app/services/categories/categories.service'
import { Store } from '@ngrx/store';
import { selectCurrentItemState } from '@app/modules/organization/store/selectors/organization.selectors';
import { selectPicklistsState } from '@app/modules/picklists/store/selectors/picklists.selectors';

@Injectable({
  providedIn: 'root'
})

export class PicklistsService {
  public endpoint: string = `${environment.apiUrl}/picklists`;
  public totalData$: State<number>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public total$: Observable<any>;
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public isAllowedToLeaveData$: State<boolean>;
  public isAllowedToLeave$: Observable<any>;
  public isPrdCodeValidData$: State<any>;
  public isPrdCodeValid$: Observable<any>;
  public currentPage;

  constructor(
    private _http: HttpClient,
    private store$: Store
  ) {
    this.totalData$ = new State(0);
    this.pageSizeData$ = new State(100);
    this.total$ = select$(this.totalData$, data => data);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.isAllowedToLeaveData$ = new State(true);
    this.isAllowedToLeave$ = select$(this.isAllowedToLeaveData$, data => data);
  }

  getPicklists(sortType, direction, query?, currentPage?, pageSize?, templates?: '1' | 'all' | undefined) {
    // Optional chaining: the picklists feature store may not be registered yet
    // (lazy module); destructuring undefined here would kill the calling effect.
    this.store$.select(selectPicklistsState).pipe(first()).subscribe(state => this.currentPage = state?.currentPage ?? 0)
    const params: any = {
      pageSize: pageSize,
      sortType: sortType,
      direction: direction,
      search: query,
      page: this.currentPage
    };
    if (templates) {
      params.templates = templates;
    }
    return this._http.get<any>(`${this.endpoint}?page=${currentPage}`, { params });
  }

  getPicklistTemplates(): Observable<any> {
    return this._http.get<any>(this.endpoint, {
      params: {
        pageSize: 100,
        sortType: 'pickListName',
        direction: 'asc',
        page: 0,
        templates: '1'
      }
    });
  }

  getPicklist(id: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${id}`)
  }

  getPicklistsPath(id: number, locationId:number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/picklists-path/${id}?locationId=${locationId}`)
  }

  printPicklists(id) {
    return this._http.get(`${this.endpoint}/pdf/${id}`, {
      responseType: 'blob'
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }


  onSave(picklist: IPicklist): Observable<any> {
    return this._http.post<any>(this.endpoint, picklist);
  }

  setPageSize(size) {
    this.pageSizeData$.next(size);
  }

  updatePicklist(picklist) {
    return this._http.put<any>(`${this.endpoint}/${picklist.pickListID}`, picklist)
  }

  onDelete(id: number) {
    return this._http.delete(`${this.endpoint}/${id}`);
  }

  searchPicklists(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search`, {
      params: {
        query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  importPicklists(picklists: {}[]) {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import`, {
      picklists
    }, { headers });
  }

  confirmImport(picklists: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirm`, {
      picklists
    });
  }

  confirmImportSkusBarcodes(skusBarcodes: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirmSkusBarcodes`, {
      skusBarcodes
    });
  }


  onSortPicklists(type: string, category?) {
    return this._http.get<any>(`${this.endpoint}/sort/${type}`, {
      params: {
        category: category
      }
    }).pipe(first()).subscribe((data) => {
      this.totalData$.next(data.total);
    });
  }

  compare(a: any | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  batchDelete(selected): Observable<any> {
    return this._http.post(`${this.endpoint}/batchDelete`, { ids: selected });
  }

  onSetIsAllowedToLeave(res) {
    return this.isAllowedToLeaveData$.next(res);
  }

  onGenerateExcel(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/picklists/export`);
  }

  onSort(arr, direction) {
    let type = 'name';

    if (arr.length <= 1) {
      return arr;
    }

    var pivot = arr[0];

    var left = [];
    var right = [];

    for (var i = 1; i < arr.length; i++) {
      if (direction == 'desc') {
        arr[i][type] < pivot[type] ? right.push(arr[i]) : left.push(arr[i]);
      } else {
        arr[i][type] < pivot[type] ? left.push(arr[i]) : right.push(arr[i]);
      }

    }

    return this.onSort(left, direction).concat(pivot, this.onSort(right, direction));
  }

  onPaginate(page) {
    this.currentPageData$.next(page);
  }

}

